import React from 'react';
import { Bill } from '../services/billService';
import { TrendingUp, DollarSign, AlertCircle, CheckCircle } from 'lucide-react';

interface RevenueChartProps {
  bills: Bill[];
}

const RevenueChart: React.FC<RevenueChartProps> = ({ bills }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Calcular métricas
  const pendingBills = bills.filter(b => b.status === 'pending');
  const paidBills = bills.filter(b => b.status === 'paid');
  const overdueBills = bills.filter(b => b.status === 'overdue');

  const totalPending = pendingBills.reduce((sum, bill) => sum + bill.total_amount, 0);
  const totalPaid = paidBills.reduce((sum, bill) => sum + bill.total_amount, 0);
  const totalOverdue = overdueBills.reduce((sum, bill) => sum + bill.total_amount, 0);
  const totalRevenue = totalPending + totalPaid + totalOverdue;

  // Próximos vencimentos (7 dias)
  const today = new Date();
  const next7Days = new Date(today);
  next7Days.setDate(next7Days.getDate() + 7);

  const upcomingBills = bills.filter(bill => {
    const dueDate = new Date(bill.due_date);
    return bill.status === 'pending' && dueDate >= today && dueDate <= next7Days;
  });

  // Receita por mês (próximos 6 meses)
  const getMonthlyRevenue = () => {
    const monthlyData: { [key: string]: number } = {};
    const months: string[] = [];

    for (let i = 0; i < 6; i++) {
      const date = new Date();
      date.setMonth(date.getMonth() + i);
      const monthKey = date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
      months.push(monthKey);
      monthlyData[monthKey] = 0;
    }

    bills.forEach(bill => {
      if (bill.status !== 'cancelled') {
        const billDate = new Date(bill.due_date);
        const monthKey = billDate.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
        if (monthlyData[monthKey] !== undefined) {
          monthlyData[monthKey] += bill.total_amount;
        }
      }
    });

    return months.map(month => ({
      month,
      value: monthlyData[month]
    }));
  };

  const monthlyRevenue = getMonthlyRevenue();
  const maxValue = Math.max(...monthlyRevenue.map(m => m.value), 1);

  return (
    <div className="space-y-6">
      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Receita Total</span>
            <TrendingUp size={20} className="text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalRevenue)}</div>
          <p className="text-xs text-gray-500 mt-1">{bills.length} cobranças</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Recebido</span>
            <CheckCircle size={20} className="text-green-600" />
          </div>
          <div className="text-2xl font-bold text-green-600">{formatCurrency(totalPaid)}</div>
          <p className="text-xs text-gray-500 mt-1">{paidBills.length} cobranças pagas</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">A Receber</span>
            <DollarSign size={20} className="text-yellow-600" />
          </div>
          <div className="text-2xl font-bold text-yellow-600">{formatCurrency(totalPending)}</div>
          <p className="text-xs text-gray-500 mt-1">{pendingBills.length} cobranças pendentes</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Vencidas</span>
            <AlertCircle size={20} className="text-red-600" />
          </div>
          <div className="text-2xl font-bold text-red-600">{formatCurrency(totalOverdue)}</div>
          <p className="text-xs text-gray-500 mt-1">{overdueBills.length} cobranças vencidas</p>
        </div>
      </div>

      {/* Gráfico de Barras */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Previsão de Receita (Próximos 6 Meses)
        </h3>
        <div className="space-y-3">
          {monthlyRevenue.map((data, index) => (
            <div key={index} className="flex items-center gap-4">
              <div className="w-20 text-sm font-medium text-gray-700">
                {data.month}
              </div>
              <div className="flex-1">
                <div className="bg-gray-200 rounded-full h-8 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-gold-400 to-gold-600 h-full flex items-center justify-end pr-2 transition-all duration-500"
                    style={{ width: `${(data.value / maxValue) * 100}%` }}
                  >
                    {data.value > 0 && (
                      <span className="text-xs font-semibold text-white">
                        {formatCurrency(data.value)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Próximos Vencimentos */}
      {upcomingBills.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Próximos Vencimentos (7 dias)
          </h3>
          <div className="space-y-3">
            {upcomingBills.slice(0, 5).map((bill) => (
              <div key={bill.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div>
                  <div className="font-medium text-gray-900">
                    {bill.subscriptions?.subscribers?.name || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-500">
                    Vence em {new Date(bill.due_date).toLocaleDateString('pt-BR')}
                  </div>
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  {formatCurrency(bill.total_amount)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueChart;
